import { WarmHandoffProcedureDate } from '@bariendo/lib/patient-access-warm-handoff-note';
import { NOT_READY_TO_COMMIT } from '@bariendo/lib/patient-access-warm-handoff-script';
import { Button, Group, Loader, Stack, Text } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { ProcedureDateOption } from './procedureDateOptions';
import { ORBIT_NEEDS_ACTION } from '@/theme/colors';

interface ProcedureDateGridProps {
  options: ProcedureDateOption[];
  loading: boolean;
  error: string | undefined;
  /** The agreed date, or the decline chip when the patient would not name one. */
  selected: WarmHandoffProcedureDate | undefined;
  declined: boolean;
  onSelect: (option: ProcedureDateOption) => void;
  onDecline: () => void;
  /**
   * Whether to offer "not ready to commit" here. Off for a script that asks that
   * question separately (the PN consultation's timing chips), so it is asked once.
   */
  allowDecline?: boolean;
  disabled?: boolean;
}

/**
 * The procedure days on offer, plus the option to record that the patient would
 * not commit.
 *
 * Days, not times — selecting a chip records what the patient agreed to and books
 * nothing. That is the difference from the intro call's slot grid, which really
 * does hold and book a consultation.
 */
export function ProcedureDateGrid({
  options,
  loading,
  error,
  selected,
  declined,
  onSelect,
  onDecline,
  allowDecline = true,
  disabled,
}: ProcedureDateGridProps) {
  if (loading) {
    return (
      <Group gap="xs" py="xs">
        <Loader size="xs" />
        <Text size="sm" c="dimmed">
          Reading the procedure schedule…
        </Text>
      </Group>
    );
  }

  return (
    <Stack gap={6} py="xs">
      {error && (
        <Group gap={6} wrap="nowrap">
          <IconAlertTriangle size={14} color={ORBIT_NEEDS_ACTION} style={{ flexShrink: 0 }} />
          <Text size="xs" c={ORBIT_NEEDS_ACTION}>
            {error}
          </Text>
        </Group>
      )}

      {!error && options.length === 0 && (
        <Text size="sm" c="dimmed">
          No procedure blocks scheduled in the next 90 days. Agree a rough timeframe and record that below.
        </Text>
      )}

      <Group gap={6}>
        {options.map((option) => {
          const isOn = selected?.start === option.date;
          return (
            <Button
              key={option.date}
              size="compact-sm"
              radius="xl"
              variant={isOn ? 'light' : 'default'}
              color={isOn ? 'green' : 'gray'}
              disabled={disabled}
              // Re-tapping the chosen date clears it, so a misclick is recoverable.
              onClick={() => onSelect(option)}
            >
              <Stack gap={0} align="center">
                <Text size="sm" fw={isOn ? 600 : 400}>
                  {option.label}
                </Text>
                {option.physicianName && (
                  <Text size="10px" c={isOn ? 'green.9' : 'dimmed'}>
                    {option.physicianName}
                  </Text>
                )}
              </Stack>
            </Button>
          );
        })}

        {/*
          Offered even with no dates on the board. A patient who will not name a
          date is a fact the CC team acts on, and an empty field cannot be told
          apart from a question nobody asked.
        */}
        {allowDecline && (
          <Button
            size="compact-sm"
            radius="xl"
            variant={declined ? 'light' : 'default'}
            color={declined ? 'green' : 'gray'}
            disabled={disabled}
            onClick={onDecline}
          >
            {NOT_READY_TO_COMMIT}
          </Button>
        )}
      </Group>
    </Stack>
  );
}
